// ─── WHAT THAT REPLY COST ────────────────────────────────────────────────────
// Haley, 2026-09-23: "show the users how many tokens were spent in each
// response ... something subtle on the message we get back from the ais like
// on the bottom of the message box and an estimated cost per response."
//
// ai-proxy returns `usage` per reply: { inputTokens, outputTokens,
// reasoningTokens, cachedTokens, costUsd, exact, model }. Cost comes from
// OpenRouter's own live rate card (see usageForDisplay in the proxy), not a
// pinned table, because DeepSeek V4 flash is $0.04/M in and $0.64/M out and
// the old estimator would have overstated a DM by ~24x.
//
// Subtle is the requirement: one dim line, no border, no icon, and it renders
// NOTHING rather than guess when the numbers are missing.

/** 1234 → "1.2k"; 980 → "980". */
export function compactTokens(n) {
  // null/undefined/'' must not become 0 — a missing count is not a zero count.
  if (n === null || n === undefined || n === '') return null;
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return null;
  if (v < 1000) return String(Math.round(v));
  if (v < 1000000) return `${(v / 1000).toFixed(v < 10000 ? 1 : 0)}k`;
  return `${(v / 1000000).toFixed(1)}M`;
}

/**
 * Money at the scale this app actually spends: a reply is fractions of a cent,
 * so two decimal places would read "$0.00" for every message and tell her
 * nothing. Sub-cent shows in tenths of a cent; a dollar or more rounds off.
 */
export function formatCost(usd) {
  // Same: an absent cost renders nothing, a genuine zero renders "$0".
  if (usd === null || usd === undefined || usd === '') return null;
  const v = Number(usd);
  if (!Number.isFinite(v) || v < 0) return null;
  if (v === 0) return '$0';
  if (v < 0.01) return `${(v * 100).toFixed(2)}¢`;
  if (v < 1) return `${(v * 100).toFixed(1)}¢`;
  return `$${v.toFixed(2)}`;
}

/**
 * The one line under a reply. Returns null when there is nothing honest to
 * show, so callers can render nothing at all.
 * @param {object} usage as returned by ai-proxy
 * @returns {{text: string, title: string}|null}
 */
export function formatUsage(usage) {
  if (!usage || typeof usage !== 'object') return null;
  const inTok = compactTokens(usage.inputTokens);
  const outTok = compactTokens(usage.outputTokens);
  if (!inTok && !outTok) return null;
  const cost = formatCost(usage.costUsd);
  const bits = [];
  if (inTok) bits.push(`${inTok} in`);
  if (outTok) bits.push(`${outTok} out`);
  if (cost) bits.push(usage.exact === false ? `~${cost}` : cost);
  const detail = [
    usage.model ? `model: ${usage.model}` : null,
    Number.isFinite(Number(usage.inputTokens)) ? `input: ${Number(usage.inputTokens).toLocaleString()} tokens` : null,
    Number.isFinite(Number(usage.outputTokens)) ? `output: ${Number(usage.outputTokens).toLocaleString()} tokens` : null,
    Number.isFinite(Number(usage.reasoningTokens)) ? `of which thinking: ${Number(usage.reasoningTokens).toLocaleString()}` : null,
    Number(usage.cachedTokens) > 0 ? `cached input: ${Number(usage.cachedTokens).toLocaleString()} (billed at the cache rate)` : null,
    usage.exact === false ? 'cost is approximate — live rates were unavailable' : 'cost from OpenRouter’s current rates',
  ].filter(Boolean).join('\n');
  return { text: bits.join(' · '), title: detail };
}

/** Inline style for the line — dim, small, and out of the way. */
export const usageLineStyle = {
  fontSize: '10px',
  opacity: 0.45,
  marginTop: '3px',
  fontFamily: "'Inter', system-ui, sans-serif",
  letterSpacing: '0.02em',
  userSelect: 'text',
  cursor: 'help',
};
